import {useCallback, useState} from 'react';
import {Typography, Badge} from 'antd';
import {KybQueueTab} from './KybQueueTab';
import {useKybStatusCounts} from './useKybStatusCounts';
import {KYB_STATUS_TABS} from './kybConstants';
import styles from './kybDashboard.module.scss';

const {Title, Text} = Typography;

/**
 * Business Verification (KYB) — the compliance queue for institutional
 * accounts.
 *
 * The status tabs are the queue's main axis; the search, country and
 * duplicate filters narrow whichever tab is open. Counts are computed against
 * those same filters so a tab badge never contradicts the table under it.
 */
export const KybDashboard = () => {
  const [filters, setFilters] = useState({
    kybStatus: 'SUBMITTED',
    country: '',
    duplicatesOnly: false,
    search: '',
  });

  const onFiltersChange = useCallback(
    patch => setFilters(current => ({...current, ...patch})),
    [],
  );

  // Counts ignore the status axis itself — each tab counts its own status
  // under the other active filters.
  const counts = useKybStatusCounts({
    country: filters.country,
    duplicatesOnly: filters.duplicatesOnly,
    search: filters.search,
  });

  return (
    <div className={styles.shell}>
      <div className={styles.pageHeader}>
        <Title level={2}>Business Verification</Title>
        <Text type="secondary">
          Review institutional accounts, their beneficial owners and the
          documents they submitted
        </Text>
      </div>

      <div className={styles.tabBar} role="tablist">
        {KYB_STATUS_TABS.map(tab => {
          const count = counts[tab.key];
          const active = filters.kybStatus === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={active}
              className={active ? styles.tabActive : styles.tabButton}
              onClick={() => onFiltersChange({kybStatus: tab.key})}>
              {tab.label}
              {typeof count === 'number' && count > 0 && (
                <Badge
                  count={count}
                  overflowCount={999}
                  className={styles.tabBadge}
                  style={{
                    backgroundColor: active ? '#8d6cfa' : '#efecfb',
                    color: active ? '#fff' : '#5b5478',
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

      <div className={styles.tabPanel} role="tabpanel">
        <KybQueueTab filters={filters} onFiltersChange={onFiltersChange} />
      </div>
    </div>
  );
};
